import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CusInput from '../../../components/material-ui/CusInput';
import TimeZone from '../../../components/shared/TimeZone';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import CusSwitch from '../../../components/material-ui/CusSwitch';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import CusTimePicker from '../../../components/material-ui/CusTimePicker';
import MailingMessage from './MailingMessage';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import CusLink from '../../../components/material-ui/CusLink';
import CusButton from '../../../components/material-ui/CusButton';
import { useLocalStorage } from 'usehooks-ts';
import { ENUM_LOCALSTORAGE } from '../../../enums/enums';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import { useAppSelector } from '../../../store';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import DaysInputs from '../../../components/material-ui/DaysInputs';

export default function SchedulerFields({
  userCount,
  groupId,
  onTestSendMailing,
  onSendMailing,
  isCreate,
  searchDone,
  readOnly,
  setIsTestSent,
  info
}) {
  const { t } = useTranslation();
  const { isMobile } = useBootstrapSize();
  const [schedulerTgId, setSchedulerTgId] = useLocalStorage(ENUM_LOCALSTORAGE.schedulerTgId, '');
  const [botId, setBotId] = useLocalStorage(ENUM_LOCALSTORAGE.schedulerBotId, null);
  const casinos = useAppSelector((state) => state.user.info.casino);

  const [isRepeatMailing, setRepeatMailing] = useState(info.isRepeatMailing);
  const [selectDays, setSelectDays] = useState(info.sendByDays || new Array(7).fill(false));
  const [createInfo, setCreateInfo] = useState(info);
  const [selectedLang, setSelectedLang] = useState('en');

  const botOptions = casinos
    .filter((el) => el.botName)
    .map((casino) => ({ label: casino.botName, value: casino.id }));

  const selectBotName = botOptions.find((el) => el.value == botId)?.label;

  useEffect(() => {
    if (botId) return;
    setBotId((prev) => prev || botOptions[0].value);
  }, [botId, botOptions]);

  return (
    <Stack gap={2} mt={4} pb={3}>
      <Stack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} gap={1}>
        <ShowContainer condition={userCount}>
          <Typography component={'span'}>
            {t('Mailing recipients found')}:
            <Typography component={'span'} fontWeight={600}>
              {userCount}
            </Typography>
          </Typography>
        </ShowContainer>

        <Box width={'fit-content'}>
          <TimeZone groupId={groupId} />
        </Box>
      </Stack>
      <Grid container spacing={2} alignItems={'center'}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <CusInput
            label={'Mailing name'}
            isRow
            value={createInfo.title}
            onChange={(e) => setCreateInfo((prev) => ({ ...prev, title: e.target.value }))}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ ml: 'auto', width: 'fit-content' }}>
            <CusSwitch
              label={'Repeat the mailing, during the period'}
              checked={isRepeatMailing}
              onChange={() => setRepeatMailing(!isRepeatMailing)}
              disabled={readOnly}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container rowSpacing={{ xs: 2 }} columnSpacing={2} alignItems={'end'}>
        <ShowContainer
          condition={isRepeatMailing}
          plug={
            <Grid size={12} mb={6.6}>
              <CusDateTimePicker
                label={'Date and time of sending'}
                disabled={isRepeatMailing || readOnly}
                value={createInfo.sendAtDate}
                onChange={(val) => setCreateInfo((prev) => ({ ...prev, sendAtDate: val }))}
              />
            </Grid>
          }>
          <Grid size={{ xs: 12, sm: 4, md: 5 }}>
            <CusDatePicker
              label={'from'}
              value={createInfo.sendDateFrom}
              onChange={(val) => setCreateInfo((prev) => ({ ...prev, sendDateFrom: val }))}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 5 }}>
            <CusDatePicker
              label={'by'}
              value={createInfo.sendDateTo}
              onChange={(val) => setCreateInfo((prev) => ({ ...prev, sendDateTo: val }))}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <CusTimePicker
              label={'time'}
              value={createInfo.sendAtTime}
              onChange={(val) => setCreateInfo((prev) => ({ ...prev, sendAtTime: val }))}
              disabled={readOnly}
            />
          </Grid>
          <Grid size={12}>
            <DaysInputs
              selectDays={selectDays}
              label={'every'}
              setSelectDays={setSelectDays}
              disabled={readOnly}
            />
          </Grid>
        </ShowContainer>
      </Grid>
      <MailingMessage
        currentLang={selectedLang}
        setCurrentLang={setSelectedLang}
        messageText={createInfo.messageList}
        setMessageText={(lang, newMessage) => {
          setCreateInfo((prev) => ({
            ...prev,
            messageList: { ...prev.messageList, [lang]: newMessage }
          }));
        }}
        disabled={readOnly}
        setIsTestSent={setIsTestSent}
      />
      <Grid container spacing={1.5} alignItems={'end'}>
        <Grid size={{ xs: 6, md: 3.5 }}>
          <CusLabelSelect
            tooltipLabelText={
              <Typography component={'span'}>
                {t(
                  'Before sending a test message, the selected bot must already be running for the recipient specified in the Telegram ID.'
                )}{' '}
                <CusLink
                  text={`@${selectBotName}`}
                  linkComponent="a"
                  target="_blank"
                  href={`https://t.me/${selectBotName}`}
                />
              </Typography>
            }
            label={'List of bots'}
            options={botOptions}
            value={botId}
            onChange={setBotId}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3.5 }}>
          <CusInput
            label={'Telegram ID'}
            value={schedulerTgId}
            onChange={(e) => setSchedulerTgId(e.target.value)}
            type="number"
            hideArrows
            tooltipText={
              <TypographyText>
                {t('You can find out your Telegram ID in the bot')}{' '}
                <CusLink
                  linkComponent={'a'}
                  href="https://t.me/userinfobot"
                  color="primary"
                  target="_blank"
                  text="@userinfobot"
                />
              </TypographyText>
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 'grow' }}>
          <Stack gap={1} direction={'row'} alignItems={'center'}>
            <CusButton
              boxProps={{ width: '100%' }}
              fullWidth
              variant="outlined"
              text="Test sending"
              disabled={!schedulerTgId}
              onClick={() =>
                onTestSendMailing({
                  botId,
                  tgUserId: +schedulerTgId,
                  messageList: createInfo.messageList
                })
              }
            />

            <CusTooltip
              text={
                'Before you save the newsletter, you need to send this message to yourself and make sure that the message is formatted correctly.'
              }
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, lg: 'auto', xl: 2 }}>
          <Stack gap={1} direction={'row'} alignItems={'center'}>
            <CusButton
              variant="contained"
              fullWidth
              text={isCreate ? 'Create a mailing' : 'Update a mailing'}
              disabled={!searchDone || readOnly}
              onClick={() => {
                const dataInfo = { ...createInfo, isRepeatMailing, sendByDays: selectDays };
                onSendMailing(dataInfo);
              }}
            />
            <CusTooltip text={'You need to perform a search before sending out the mailing'} />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
